import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Box,
	Button,
	FormControl,
	FormLabel,
	HStack,
	IconButton,
	Input,
	Select,
	Stack,
	Text,
	Textarea,
	useToast,
} from "@chakra-ui/react";
import { FiPlus, FiTrash2, FiArrowRight } from "react-icons/fi";
import { useMutation } from "@apollo/client";
import {
	EducationLevel,
	PrefillEducationInput,
	PrefillEmploymentInput,
	PrefillWorkHistoryInput,
	PrefillCertificationInput,
} from "src/__generated__/graphql";
import { PREFILL_PORTFOLIO_FROM_CV } from "./graphql";
import { ResumeData } from "./types";
import { logEvent } from "src/utlis/analytics";
import isEqual from "lodash/isEqual";

const QUALIFICATION_LABELS: Record<string, string> = {
	JSSCE: "Junior Secondary School Certificate (JSSCE)",
	SSCE: "Senior School Certificate (SSCE)",
	NABTEB: "NABTEB Certificate",
	KCSE: "KCSE",
	OND: "Ordinary National Diploma (OND)",
	HND: "Higher National Diploma (HND)",
	BACHELORS: "Bachelor's Degree",
	MASTERS: "Master's Degree",
	DOCTORATE: "Doctorate Degree",
};

function mapQualificationToEnum(q: string): EducationLevel {
	const mapping: Record<string, EducationLevel> = {
		JSSCE: EducationLevel.Jssce,
		SSCE: EducationLevel.Ssce,
		NABTEB: EducationLevel.Nabteb,
		KCSE: EducationLevel.Kcse,
		OND: EducationLevel.Ond,
		HND: EducationLevel.Hnd,
		BACHELORS: EducationLevel.Bachelors,
		MASTERS: EducationLevel.Masters,
		DOCTORATE: EducationLevel.Doctorate,
	};
	return mapping[q] || EducationLevel.Ssce;
}

interface PrefillReviewProps {
	resumeData: ResumeData;
	onComplete: () => void;
	onStartOver: () => void;
}

export const PrefillReview: React.FC<PrefillReviewProps> = ({
	resumeData,
	onComplete,
	onStartOver,
}) => {
	const toast = useToast();

	const [professionalSummary, setProfessionalSummary] = useState(
		resumeData.professionalSummary || ""
	);

	const [educationItems, setEducationItems] = useState<
		PrefillEducationInput[]
	>(
		resumeData.educationItems.map((e) => ({
			schoolName: e.schoolName,
			fieldOfStudy: e.fieldOfStudy,
			qualification: mapQualificationToEnum(e.qualification),
		}))
	);

	const [employmentItems, setEmploymentItems] = useState<
		PrefillEmploymentInput[]
	>(
		resumeData.employmentHistoryItems.map((e) => ({
			companyName: e.companyName,
			jobTitle: e.jobTitle,
			description: e.description,
			startedAt: e.startedAt,
			endedAt: e.endedAt,
		}))
	);

	const [workHistoryItems, setWorkHistoryItems] = useState<
		PrefillWorkHistoryInput[]
	>(
		resumeData.projectHistoryItems.map((p) => ({
			name: p.name,
			description: p.description,
			startedAt: p.startedAt,
			endedAt: p.endedAt,
		}))
	);

	const [certificationItems, setCertificationItems] = useState<
		PrefillCertificationInput[]
	>(
		resumeData.certificationItems.map((c) => ({
			name: c.name,
			issuedBy: c.issuedBy,
			issuedAt: c.issuedAt,
			issuerCertificationId: c.issuerCertificationId,
			url: c.url,
		}))
	);

	// Snapshot for detecting modifications (analytics)
	const originalData = useRef<{
		professionalSummary: string;
		educationItems: PrefillEducationInput[];
		employmentItems: PrefillEmploymentInput[];
		workHistoryItems: PrefillWorkHistoryInput[];
		certificationItems: PrefillCertificationInput[];
	} | null>(null);
	const [deletedCount, setDeletedCount] = useState(0);

	useEffect(() => {
		logEvent("cv_review_screen_viewed", { platform: "web" });
	}, []);

	// Capture original data once on first render
	useEffect(() => {
		if (!originalData.current) {
			originalData.current = structuredClone({
				professionalSummary,
				educationItems,
				employmentItems,
				workHistoryItems,
				certificationItems,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const [prefillPortfolio, { loading }] = useMutation(
		PREFILL_PORTFOLIO_FROM_CV,
		{
			onCompleted: (data) => {
				if (data.prefillPortfolioFromCV.laborpassCompleted) {
					logEvent("laborpass_completed", {
						completion_path: "cv",
						platform: "web",
					});
				}

				toast({
					title: "Portfolio updated",
					description:
						"Your CV details have been saved to your LaborPass.",
					status: "success",
					duration: 5000,
					isClosable: true,
				});
				onComplete();
			},
			onError: () => {
				toast({
					title: "Failed to save",
					description: "Something went wrong. Please try again.",
					status: "error",
					duration: 5000,
					isClosable: true,
				});
			},
			refetchQueries: ["MyPortfolioWithMedia"],
			awaitRefetchQueries: true,
		}
	);

	const isValid = useMemo(() => {
		for (const edu of educationItems) {
			if (!edu.schoolName.trim()) return false;
		}
		for (const emp of employmentItems) {
			if (!emp.companyName.trim() || !emp.jobTitle.trim()) return false;
		}
		for (const wh of workHistoryItems) {
			if (!wh.name.trim()) return false;
		}
		if (professionalSummary && professionalSummary.trim().length < 50) {
			return false;
		}
		return true;
	}, [educationItems, employmentItems, workHistoryItems, professionalSummary]);

	const handleSubmit = useCallback(() => {
		logEvent("cv_data_confirmed", { platform: "web" });

		// Only fire edit/delete events if form was actually modified
		if (originalData.current) {
			const currentData = {
				professionalSummary,
				educationItems,
				employmentItems,
				workHistoryItems,
				certificationItems,
			};
			if (!isEqual(originalData.current, currentData)) {
				logEvent("cv_fields_edited_post_extraction", {
					platform: "web",
				});
			}
		}
		if (deletedCount > 0) {
			logEvent("cv_entries_deleted_post_extraction", {
				entries_deleted_count: deletedCount,
				platform: "web",
			});
		}

		prefillPortfolio({
			variables: {
				professionalSummary: professionalSummary || undefined,
				educationItems,
				employmentHistoryItems: employmentItems,
				workHistoryItems: workHistoryItems,
				certificationItems,
			},
		});
	}, [
		prefillPortfolio,
		professionalSummary,
		educationItems,
		employmentItems,
		workHistoryItems,
		certificationItems,
		deletedCount,
	]);

	// Helper for updating items in arrays
	const updateItem = <T,>(
		items: T[],
		index: number,
		field: keyof T,
		value: T[keyof T],
		setter: React.Dispatch<React.SetStateAction<T[]>>
	) => {
		setter(
			items.map((item, i) =>
				i === index ? { ...item, [field]: value } : item
			)
		);
	};

	const removeItem = <T,>(
		items: T[],
		index: number,
		setter: React.Dispatch<React.SetStateAction<T[]>>
	) => {
		setter(items.filter((_, i) => i !== index));
		setDeletedCount((c) => c + 1);
	};

	return (
		<Stack
			spacing={0}
			className="flex flex-col lg:border-[1px] border-[#C9C9C9] bg-white h-auto lg:min-h-[70vh] justify-between"
		>
			<Stack
				spacing={6}
				px={{ base: 4, lg: 8 }}
				py={{ base: 6, lg: 12 }}
				overflowY="auto"
				flex={1}
			>
				<Box>
					<Text fontSize="2xl" fontWeight="bold" color="#121212">
						Review your CV details
					</Text>
					<Text color="gray.600" mt={1}>
						Review and edit the information extracted from your CV before saving
						it to your LaborPass.
					</Text>
				</Box>

				{/* Professional Summary */}
				<SectionCard
					title="Professional Summary"
					extracted={!!resumeData.professionalSummary}
				>
					<FormControl>
						<Textarea
							value={professionalSummary}
							onChange={(e) => setProfessionalSummary(e.target.value)}
							placeholder="Brief professional summary (min 50 characters)"
							rounded={0}
							border="1px solid #D7D7D7"
							rows={5}
						/>
						{professionalSummary && professionalSummary.trim().length < 50 && (
							<Text fontSize="xs" color="orange.500" mt={1}>
								Minimum 50 characters ({professionalSummary.trim().length}/50)
							</Text>
						)}
					</FormControl>
				</SectionCard>

				{/* Employment History */}
				<SectionCard
					title="Employment History"
					extracted={resumeData.employmentHistoryItems.length > 0}
					onAdd={() =>
						setEmploymentItems([
							...employmentItems,
							{
								companyName: "",
								jobTitle: "",
								description: "",
								startedAt: "",
							},
						])
					}
				>
					{employmentItems.map((item, index) => (
						<Box key={index} p={4} border="1px solid #E7E7E7" mb={3}>
							<HStack justify="space-between" mb={3}>
								<Text fontWeight="medium" fontSize="sm">
									Employment {index + 1}
								</Text>
								<IconButton
									aria-label="Remove"
									icon={<FiTrash2 />}
									size="sm"
									variant="ghost"
									colorScheme="red"
									onClick={() =>
										removeItem(employmentItems, index, setEmploymentItems)
									}
								/>
							</HStack>
							<Stack spacing={3}>
								<HStack spacing={3}>
									<FormControl>
										<FormLabel fontSize="sm">Company</FormLabel>
										<Input
											value={item.companyName}
											onChange={(e) =>
												updateItem(
													employmentItems,
													index,
													"companyName",
													e.target.value,
													setEmploymentItems
												)
											}
											rounded={0}
											border="1px solid #D7D7D7"
											size="sm"
										/>
									</FormControl>
									<FormControl>
										<FormLabel fontSize="sm">Job Title</FormLabel>
										<Input
											value={item.jobTitle}
											onChange={(e) =>
												updateItem(
													employmentItems,
													index,
													"jobTitle",
													e.target.value,
													setEmploymentItems
												)
											}
											rounded={0}
											border="1px solid #D7D7D7"
											size="sm"
										/>
									</FormControl>
								</HStack>
								<FormControl>
									<FormLabel fontSize="sm">Description</FormLabel>
									<Textarea
										value={item.description}
										onChange={(e) =>
											updateItem(
												employmentItems,
												index,
												"description",
												e.target.value,
												setEmploymentItems
											)
										}
										rounded={0}
										border="1px solid #D7D7D7"
										size="sm"
										rows={4}
									/>
								</FormControl>
								<Stack spacing={3} direction={{ base: "column", md: "row" }}>
									<FormControl>
										<FormLabel fontSize="sm">Start Date</FormLabel>
										<Input
											type="date"
											value={item.startedAt}
											onChange={(e) =>
												updateItem(
													employmentItems,
													index,
													"startedAt",
													e.target.value,
													setEmploymentItems
												)
											}
											rounded={0}
											border="1px solid #D7D7D7"
											size="sm"
										/>
									</FormControl>
									<FormControl>
										<FormLabel fontSize="sm">End Date</FormLabel>
										<Input
											type="date"
											value={item.endedAt || ""}
											onChange={(e) =>
												updateItem(
													employmentItems,
													index,
													"endedAt",
													e.target.value || undefined,
													setEmploymentItems
												)
											}
											rounded={0}
											border="1px solid #D7D7D7"
											size="sm"
										/>
									</FormControl>
								</Stack>
							</Stack>
						</Box>
					))}
					{employmentItems.length === 0 && (
						<Text fontSize="sm" color="gray.500">
							No employment history extracted. Click + to add.
						</Text>
					)}
				</SectionCard>

				{/* Work History (Projects) */}
				<SectionCard
					title="Project History"
					extracted={resumeData.projectHistoryItems.length > 0}
					onAdd={() =>
						setWorkHistoryItems([
							...workHistoryItems,
							{ name: "", description: "" },
						])
					}
				>
					{workHistoryItems.map((item, index) => (
						<Box key={index} p={4} border="1px solid #E7E7E7" mb={3}>
							<HStack justify="space-between" mb={3}>
								<Text fontWeight="medium" fontSize="sm">
									Project {index + 1}
								</Text>
								<IconButton
									aria-label="Remove"
									icon={<FiTrash2 />}
									size="sm"
									variant="ghost"
									colorScheme="red"
									onClick={() =>
										removeItem(workHistoryItems, index, setWorkHistoryItems)
									}
								/>
							</HStack>
							<Stack spacing={3}>
								<FormControl>
									<FormLabel fontSize="sm">Project Name</FormLabel>
									<Input
										value={item.name}
										onChange={(e) =>
											updateItem(
												workHistoryItems,
												index,
												"name",
												e.target.value,
												setWorkHistoryItems
											)
										}
										rounded={0}
										border="1px solid #D7D7D7"
										size="sm"
									/>
								</FormControl>
								<FormControl>
									<FormLabel fontSize="sm">Description</FormLabel>
									<Textarea
										value={item.description}
										onChange={(e) =>
											updateItem(
												workHistoryItems,
												index,
												"description",
												e.target.value,
												setWorkHistoryItems
											)
										}
										rounded={0}
										border="1px solid #D7D7D7"
										size="sm"
										rows={4}
									/>
								</FormControl>
								<Stack spacing={3} direction={{ base: "column", md: "row" }}>
									<FormControl>
										<FormLabel fontSize="sm">Start Date</FormLabel>
										<Input
											type="date"
											value={item.startedAt || ""}
											onChange={(e) =>
												updateItem(
													workHistoryItems,
													index,
													"startedAt",
													e.target.value || undefined,
													setWorkHistoryItems
												)
											}
											rounded={0}
											border="1px solid #D7D7D7"
											size="sm"
										/>
									</FormControl>
									<FormControl>
										<FormLabel fontSize="sm">End Date</FormLabel>
										<Input
											type="date"
											value={item.endedAt || ""}
											onChange={(e) =>
												updateItem(
													workHistoryItems,
													index,
													"endedAt",
													e.target.value || undefined,
													setWorkHistoryItems
												)
											}
											rounded={0}
											border="1px solid #D7D7D7"
											size="sm"
										/>
									</FormControl>
								</Stack>
							</Stack>
						</Box>
					))}
					{workHistoryItems.length === 0 && (
						<Text fontSize="sm" color="gray.500">
							No projects extracted. Click + to add.
						</Text>
					)}
				</SectionCard>

				{/* Education */}
				<SectionCard
					title="Education"
					extracted={resumeData.educationItems.length > 0}
					onAdd={() =>
						setEducationItems([
							...educationItems,
							{
								schoolName: "",
								qualification: EducationLevel.Ssce,
							},
						])
					}
				>
					{educationItems.map((item, index) => (
						<Box key={index} p={4} border="1px solid #E7E7E7" mb={3}>
							<HStack justify="space-between" mb={3}>
								<Text fontWeight="medium" fontSize="sm">
									Education {index + 1}
								</Text>
								<IconButton
									aria-label="Remove"
									icon={<FiTrash2 />}
									size="sm"
									variant="ghost"
									colorScheme="red"
									onClick={() =>
										removeItem(educationItems, index, setEducationItems)
									}
								/>
							</HStack>
							<Stack spacing={3}>
								<FormControl>
									<FormLabel fontSize="sm">School Name</FormLabel>
									<Input
										value={item.schoolName}
										onChange={(e) =>
											updateItem(
												educationItems,
												index,
												"schoolName",
												e.target.value,
												setEducationItems
											)
										}
										rounded={0}
										border="1px solid #D7D7D7"
										size="sm"
									/>
								</FormControl>
								<FormControl>
									<FormLabel fontSize="sm">Qualification</FormLabel>
									<Select
										value={item.qualification}
										onChange={(e) =>
											updateItem(
												educationItems,
												index,
												"qualification",
												e.target.value as EducationLevel,
												setEducationItems
											)
										}
										rounded={0}
										border="1px solid #D7D7D7"
										size="sm"
									>
										{Object.entries(QUALIFICATION_LABELS).map(
											([key, label]) => (
												<option key={key} value={key}>
													{label}
												</option>
											)
										)}
									</Select>
								</FormControl>
								<FormControl>
									<FormLabel fontSize="sm">Field of Study</FormLabel>
									<Input
										value={item.fieldOfStudy || ""}
										onChange={(e) =>
											updateItem(
												educationItems,
												index,
												"fieldOfStudy",
												e.target.value || undefined,
												setEducationItems
											)
										}
										placeholder="Optional"
										rounded={0}
										border="1px solid #D7D7D7"
										size="sm"
									/>
								</FormControl>
							</Stack>
						</Box>
					))}
					{educationItems.length === 0 && (
						<Text fontSize="sm" color="gray.500">
							No education extracted. Click + to add.
						</Text>
					)}
				</SectionCard>

				{/* Certifications */}
				<SectionCard
					title="Certifications"
					extracted={resumeData.certificationItems.length > 0}
					onAdd={() =>
						setCertificationItems([
							...certificationItems,
							{ name: "", issuedBy: "", issuedAt: "" },
						])
					}
				>
					{certificationItems.map((item, index) => (
						<Box key={index} p={4} border="1px solid #E7E7E7" mb={3}>
							<HStack justify="space-between" mb={3}>
								<Text fontWeight="medium" fontSize="sm">
									Certification {index + 1}
								</Text>
								<IconButton
									aria-label="Remove"
									icon={<FiTrash2 />}
									size="sm"
									variant="ghost"
									colorScheme="red"
									onClick={() =>
										removeItem(certificationItems, index, setCertificationItems)
									}
								/>
							</HStack>
							<Stack spacing={3}>
								<HStack spacing={3}>
									<FormControl>
										<FormLabel fontSize="sm">Name</FormLabel>
										<Input
											value={item.name}
											onChange={(e) =>
												updateItem(
													certificationItems,
													index,
													"name",
													e.target.value,
													setCertificationItems
												)
											}
											rounded={0}
											border="1px solid #D7D7D7"
											size="sm"
										/>
									</FormControl>
									<FormControl>
										<FormLabel fontSize="sm">Issuer</FormLabel>
										<Input
											value={item.issuedBy}
											onChange={(e) =>
												updateItem(
													certificationItems,
													index,
													"issuedBy",
													e.target.value,
													setCertificationItems
												)
											}
											rounded={0}
											border="1px solid #D7D7D7"
											size="sm"
										/>
									</FormControl>
								</HStack>
								<FormControl>
									<FormLabel fontSize="sm">Issue Date</FormLabel>
									<Input
										type="date"
										value={item.issuedAt}
										onChange={(e) =>
											updateItem(
												certificationItems,
												index,
												"issuedAt",
												e.target.value,
												setCertificationItems
											)
										}
										rounded={0}
										border="1px solid #D7D7D7"
										size="sm"
									/>
								</FormControl>
							</Stack>
						</Box>
					))}
					{certificationItems.length === 0 && (
						<Text fontSize="sm" color="gray.500">
							No certifications extracted. Click + to add.
						</Text>
					)}
				</SectionCard>
			</Stack>

			{/* Footer */}
			<Stack
				py={{ base: 4, lg: 8 }}
				px={{ base: 4, lg: 8 }}
				borderTop={{ base: "none", lg: "1px solid #E7E7E7" }}
				direction={{ base: "column", lg: "row" }}
				spacing={4}
				justifyContent="space-between"
				alignItems={{ base: "stretch", lg: "center" }}
			>
				<Button
					variant="ghost"
					onClick={() => {
						logEvent("cv_path_abandoned", {
							abandon_stage: "review",
							platform: "web",
						});
						onStartOver();
					}}
					isDisabled={loading}
					size="sm"
					w={{ base: "full", lg: "auto" }}
				>
					Start over with manual fill
				</Button>
				<Button
					colorScheme="brand"
					rounded={0}
					px={8}
					py={6}
					rightIcon={<FiArrowRight size="18px" />}
					onClick={handleSubmit}
					isDisabled={!isValid}
					isLoading={loading}
					w={{ base: "full", lg: "auto" }}
				>
					Confirm & Save to LaborPass
				</Button>
			</Stack>
		</Stack>
	);
};

// Helper component for section cards
interface SectionCardProps {
	title: string;
	extracted: boolean;
	onAdd?: () => void;
	children: React.ReactNode;
}

const SectionCard: React.FC<SectionCardProps> = ({
	title,
	extracted,
	onAdd,
	children,
}) => (
	<Box border={{ base: "none", md: "1px solid #E7E7E7" }} p={{ base: 0, md: 4 }}>
		<HStack justify="space-between" mb={4}>
			<Text fontWeight="bold" fontSize="lg">
				{title}
			</Text>
			{onAdd && (
				<Button
					leftIcon={<FiPlus size="18px" />}
					colorScheme="earthRed"
					size="sm"
					variant="outline"
					onClick={onAdd}
					rounded={0}
				>
					Add
				</Button>
			)}
		</HStack>
		{children}
	</Box>
);
